import { Link } from "@inertiajs/react"

import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

/**
 * The three screens of the group workspace.
 *
 * Tabs rather than a second sidebar. The staff sidebar already owns the
 * left edge and belongs to the *location* you are operating; a second rail for
 * the group would put two navigations of different scopes side by side, and the
 * reader would have to work out which one they were in. Four destinations do
 * not need a rail anyway.
 *
 * `settings` is absent, not disabled, for anyone who may not configure the
 * organization — a disabled tab invites the reader to work out what would
 * unlock it, and nothing they can do here would. Owners and admins both reach
 * it; handing out access is the owner's alone and lives on the members screen.
 */

export type WorkspaceTab = "overview" | "branches" | "members" | "settings"

const TABS: { key: WorkspaceTab; href: string; adminOnly: boolean }[] = [
  { key: "overview", href: "/organization", adminOnly: false },
  { key: "branches", href: "/organization/branches", adminOnly: false },
  { key: "members", href: "/organization/members", adminOnly: false },
  { key: "settings", href: "/organization/settings", adminOnly: true },
]

export function OrganizationWorkspaceNav({
  current,
  organizationName,
  canManageOrganization,
}: {
  current: WorkspaceTab
  organizationName: string
  canManageOrganization: boolean
}) {
  const visible = TABS.filter((tab) => !tab.adminOnly || canManageOrganization)

  return (
    <>
      {/* The heading is here and invisible, rather than a `PageHeader` above the
          tabs, because on screen it would say for the third time what the
          switcher and the active tab already say. A document still needs an h1,
          and a screen reader user still needs to be told which group and which
          section without inferring it from a button and a tab list. */}
      <h1 className="sr-only">
        {organizationName} ·{" "}
        {t(`ui.react.pages.staff.location.organization.nav.${current}`)}
      </h1>

      <nav
        className="border-border flex flex-wrap items-center gap-1 border-b"
        aria-label={t("ui.react.pages.staff.location.organization.nav.label")}
        data-testid="organization-workspace-nav"
      >
        {visible.map((tab) => {
          const active = tab.key === current

          return (
            <Link
              key={tab.key}
              href={tab.href}
              aria-current={active ? "page" : undefined}
              data-testid={`organization-tab-${tab.key}`}
              className={cn(
                // -1px so the active underline sits on the container's border
                // rather than beside it.
                "-mb-px flex h-11 items-center border-b-2 px-3 text-sm transition-colors md:h-9",
                active
                  ? "border-foreground text-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground border-transparent"
              )}
            >
              {t(`ui.react.pages.staff.location.organization.nav.${tab.key}`)}
            </Link>
          )
        })}
      </nav>
    </>
  )
}
